import React, { useState, useEffect, useCallback } from "react";
import { Layout, message, Table, Tag, Button, Space, Tabs, Row, Col, DatePicker, Select, Input, Breadcrumb } from "antd";
import { CheckCircleOutlined, EyeOutlined, SearchOutlined, ClearOutlined, FilterOutlined, HomeOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useNavigate } from "react-router-dom";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PRApprovals = () => {
    const [approvals, setApprovals] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    
    // Filters state
    const [supplierFilter, setSupplierFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState([]);
    const [search, setSearch] = useState("");
    
    // Applied filters state
    const [appliedSupplier, setAppliedSupplier] = useState(undefined);
    const [appliedDateRange, setAppliedDateRange] = useState([]);
    const [appliedSearch, setAppliedSearch] = useState("");

    const navigate = useNavigate();

    // Permission check using localStorage (matching current project pattern)
    const isDisabled = (permName) => {
        const value = localStorage.getItem(`USERPERMISSION_${permName}`);
        if (value === null) return true;
        return value !== 'false';
    };

    const fetchApprovals = useCallback(async (
        page = 1,
        limit = 10,
        tabKey = activeTab,
        supplierId = appliedSupplier,
        dates = appliedDateRange,
        searchText = appliedSearch
    ) => {
        setLoading(true);
        try {
            const startDate = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : "";
            const endDate = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : "";
            
            const response = await api.post(`/inventory/pr/list_approvals.php`, {
                tab: tabKey,
                supplier_id: supplierId ? parseInt(supplierId) : 0,
                start_date: startDate,
                end_date: endDate,
                search: searchText || "",
                page,
                limit
            });
            if (response.data.status === "success") {
                setApprovals(response.data.dataset || []);
                setPagination({
                    current: page,
                    pageSize: limit,
                    total: response.data.total || 0,
                });
            }
            
            const resSups = await api.get("/masterdropdown/listof_activesuppliers.php");
            if (resSups.data && resSups.data.status === "success") {
                setSuppliers(resSups.data.dataset);
            }
        } catch (error) {
            message.error("Failed to fetch Purchase Request approvals.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, appliedSupplier, appliedDateRange, appliedSearch]);

    useEffect(() => {
        fetchApprovals(1, pagination.pageSize, activeTab, appliedSupplier, appliedDateRange, appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, appliedSupplier, appliedDateRange, appliedSearch]);

    const handleFilter = () => {
        setAppliedSupplier(supplierFilter);
        setAppliedDateRange(dateRange);
        setAppliedSearch(search.trim());
        fetchApprovals(1, pagination.pageSize, activeTab, supplierFilter, dateRange, search.trim());
    };

    const handleClear = () => {
        setSupplierFilter(undefined);
        setDateRange([]);
        setSearch("");
        setAppliedSupplier(undefined);
        setAppliedDateRange([]);
        setAppliedSearch("");
        fetchApprovals(1, pagination.pageSize, activeTab, undefined, [], "");
    };

    const handleTableChange = (pag) => {
        fetchApprovals(pag.current, pag.pageSize, activeTab, appliedSupplier, appliedDateRange, appliedSearch);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    const columns = [
        {
            title: "PR Reference",
            dataIndex: "pr_ref",
            key: "pr_ref",
            render: (text, record) => {
                const canView = !isDisabled('inventory_purchase_requests');
                return (
                    <span 
                        style={{ 
                            color: canView ? 'var(--fluid-primary)' : '#888', 
                            cursor: canView ? 'pointer' : 'not-allowed', 
                            fontWeight: 600
                        }} 
                        onClick={() => {
                            if (canView) {
                                navigate(`/inventory/pr/${record.pr_id}/view`);
                            } else {
                                message.warning("You do not have permission to view details.");
                            }
                        }}
                    >
                        {text}
                    </span>
                );
            }
        },
        {
            title: "Supplier",
            dataIndex: "supplier_name",
            key: "supplier_name"
        },
        {
            title: "Date",
            dataIndex: "pr_date",
            key: "pr_date",
            render: (val) => val ? dayjs(val).format('YYYY-MM-DD') : '-'
        },
        {
            title: "Items",
            dataIndex: "total_items_count",
            key: "total_items_count",
            align: 'center'
        },
        {
            title: "Grand Total",
            dataIndex: "pr_final_amount",
            key: "pr_final_amount",
            align: 'right',
            render: (val) => <strong>LKR {parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
        },
        {
            title: "Status",
            dataIndex: "pr_status",
            key: "pr_status",
            render: (status) => {
                const text = (status === 'Pending Approval' || status === 'Pending') ? 'Pending' : status;
                return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{text}</Tag>;
            }
        },
        {
            title: "Requested By",
            dataIndex: "creator_name",
            key: "creator_name",
            render: (val) => val || '-'
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={record.pr_status === 'Pending' || record.pr_status === 'Pending Approval' ? <CheckCircleOutlined /> : <EyeOutlined />}
                        size="small"
                        onClick={() => navigate(`/inventory/pr/approve/${record.pr_id}`)}
                        className={record.pr_status === 'Pending' || record.pr_status === 'Pending Approval' ? "fluid-btn-primary" : ""}
                    >
                        {record.pr_status === 'Pending' || record.pr_status === 'Pending Approval' ? 'Review' : 'View'}
                    </Button>
                </Space>
            )
        }
    ];

    const tabItems = [
        { key: 'pending', label: 'Pending Approval' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />
            
            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/inventory-items', title: 'Inventory' },
                                    { title: 'PR Approvals' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <CheckCircleOutlined /> PR Approvals
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Review, approve or reject purchase requests</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div style={{ padding: "16px 16px 0" }}>
                        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
                    </div>

                    {/* Advanced filter bar */}
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="PR ref, supplier name"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier:</div>
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) => {
                                      const label = option?.label || option?.children;
                                      if (Array.isArray(label)) {
                                        return label.join('').toLowerCase().includes(input.toLowerCase());
                                      }
                                      return String(label || '').toLowerCase().includes(input.toLowerCase());
                                    }}
                                    value={supplierFilter}
                                    onChange={setSupplierFilter}
                                    style={{ width: "100%" }}
                                    placeholder="Choose Supplier"
                                    allowClear
                                >
                                    {suppliers.map(sup => (
                                        <Option key={sup.value} value={Number(sup.value)}>{sup.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker 
                                    value={dateRange}
                                    onChange={setDateRange} 
                                    style={{ width: "100%" }} 
                                />
                            </Col>
                            <Col xs={24} sm={24} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button 
                                        type="primary" 
                                        icon={<FilterOutlined />} 
                                        onClick={handleFilter}
                                        className="fluid-btn-primary"
                                    >
                                        Filter
                                    </Button>
                                    <Button 
                                        icon={<ClearOutlined />} 
                                        onClick={handleClear}
                                        className="fluid-btn-secondary"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={columns}
                            dataSource={approvals}
                            rowKey="pr_id"
                            loading={loading}
                            bordered
                            size="small"
                            scroll={{ x: 'max-content' }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                            }}
                            onChange={handleTableChange}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default PRApprovals;
